/**
 * Renderer Behavior Editor
 * Shows all material slots with live previews, lets users customize or restore defaults.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { MaterialData, RendererData } from 'trace-viewer/behaviors';
import { Renderer, isPropertyBinding, boundPropertyName } from 'trace-viewer/behaviors';
import {
    MaterialPreviews,
    setMaterialSrcForState,
    setMaterialColorForState,
    clearStateValue,
    editTargetState,
    getMaterialVariantSet,
    createMaterialVariantSet,
    updateVariantOption,
    getUndoManager,
} from 'trace-viewer/editor';
import type { MaterialSlot } from 'trace-viewer/editor';
import type { SceneObject } from 'trace-viewer/scene';
import { EntityType } from 'trace-viewer/cms';
import { resolveBoundSrcDisplay, resolveBoundColorDisplay } from './material-binding-ui';
import { CatalogBrowseButton } from '../components/catalog-asset-picker';
import { SlotVariantsRow } from './slot-variants-row';
import { ColorBindingSelector } from './color-binding-selector';
import { copyForInsideEdit, needsReferenceCopy } from '../cms/reference-edit-guard';
import { EDITOR_EVENTS } from '../editor-events';

/** Preview thumbnail render size — displayed at 42×42px so 128px is plenty */
const PREVIEW_SIZE = 128;

// ============================================================================
// Styles
// ============================================================================

const styles = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    label: {
        fontSize: '11px',
        color: '#999',
        minWidth: '70px',
        flexShrink: 0,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '6px',
    } as React.CSSProperties,
    section: {
        marginBottom: '12px',
    } as React.CSSProperties,
    sectionHeader: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#888',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '8px',
        paddingBottom: '4px',
        borderBottom: '1px solid #3a3a3a',
    } as React.CSSProperties,
};

// ============================================================================
// Material Slot Card
// ============================================================================

interface SlotCardProps {
    slot: MaterialSlot;
    override: MaterialData | undefined;
    /** Non-routed field changes (UV scale, whole-slot restore). */
    onOverrideChange: (updated: MaterialData | undefined) => void;
    /** State-aware color pick (auto-binds + routes to the active state). */
    onColorSelect: (hex: string) => void;
    /** State-aware material-src pick (auto-binds + routes to the active state). */
    onSrcSelect: (url: string) => void;
    /** State-aware clear of the color (`'baseColorFactor'`) or src (`'src'`) field. */
    onClearField: (field: 'baseColorFactor' | 'src') => void;
    /** "Variants" action — create (or adopt) a variant set on this slot's color. */
    onCreateVariants: () => void;
    /** Debounced slot-preview regen (shared with the color/src routed edits). */
    onRegen: () => void;
    /** A bind/rebind/unbind changed the slot's binding — re-detect + regen. */
    onBindingChanged: () => void;
    sceneObject: SceneObject;
}

const SlotCard: React.FC<SlotCardProps> = ({ slot, override, onOverrideChange, onColorSelect, onSrcSelect, onClearField, onCreateVariants, onRegen, onBindingChanged, sceneObject }) => {
    const [expanded, setExpanded] = useState(false);
    // Bound slot ("src": "$material"): the Source field shows the bound
    // property's RESOLVED url, not the binding string. Edits are buffered
    // locally and committed on blur/Enter — the routed write round-trips
    // through the Properties commit, too slow for per-keystroke commits.
    const { boundTo, displaySrc } = resolveBoundSrcDisplay(sceneObject, override?.src);
    const { boundTo: colorBoundTo, displayColor: resolvedBoundColor } =
        resolveBoundColorDisplay(sceneObject, override?.baseColorFactor);
    const [srcDraft, setSrcDraft] = useState<string | null>(null);
    // Color hex field buffers keystrokes locally (like srcDraft) so partial input
    // ("#3", "#33...") isn't reverted by the controlled value; commits on
    // blur/Enter once it's a valid #rrggbb[aa].
    const [colorDraft, setColorDraft] = useState<string | null>(null);
    // A field is "set" when it carries a literal override OR is bound (bound =
    // state-swappable, still clearable back to Base).
    const hasColorOverride = override?.baseColorFactor != null;
    const hasGlbOverride = override?.src != null;
    const hasUvOverride = override?.texture_transform != null;
    const isCustomized = hasColorOverride || hasGlbOverride || hasUvOverride;

    const parseColor = (color: string | undefined): string => {
        if (!color) return '#888888';
        // Handle #RRGGBBAA format
        if (color.length === 9) return color.substring(0, 7);
        return color.length === 7 ? color : '#888888';
    };

    // Bound slot shows the bound property's resolved value; unbound shows the
    // literal override; falls back to the detected albedo.
    const displayColor = colorBoundTo
        ? (resolvedBoundColor ? parseColor(resolvedBoundColor) : undefined)
        : hasColorOverride
            ? parseColor(override.baseColorFactor)
            : slot.albedoColor ? parseColor(slot.albedoColor) : undefined;

    const handleUvScaleChange = (value: number) => {
        const base: MaterialData = override ?? { targetMaterialIndex: slot.materialIndex ?? 0 };
        onOverrideChange({
            ...base,
            texture_transform: { scale: [value, value] },
        });
    };

    const handleClearUv = () => {
        if (!override) return;
        const { texture_transform: _, ...rest } = override;
        const hasOtherProps = rest.src != null || rest.baseColorFactor != null;
        onOverrideChange(hasOtherProps ? rest : undefined);
    };

    const handleRestore = () => {
        onOverrideChange(undefined);
    };

    const uvScale = override?.texture_transform?.scale?.[0] ?? slot.uvScale ?? 1;

    // Variant set for this slot's color (null when the slot isn't bound to a
    // variants-bearing color declaration). Re-read whenever the override src for
    // baseColorFactor changes; the row itself refreshes on BEHAVIOR_CHANGED.
    const slotIndex = slot.materialIndex ?? 0;
    const variantSet = getMaterialVariantSet(sceneObject, slotIndex);

    return (
        <div style={{
            backgroundColor: '#1e1e1e',
            borderRadius: '4px',
            marginBottom: '4px',
            overflow: 'hidden',
            border: isCustomized ? '1px solid #4a4a4a' : '1px solid #2a2a2a',
        }}>
            {/* Header row: preview + name + badges */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                {/* Preview thumbnail */}
                <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    backgroundColor: '#111',
                    border: '1px solid #333',
                }}>
                    {slot.previewImage ? (
                        <img
                            src={slot.previewImage}
                            alt={slot.slotName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    ) : displayColor ? (
                        <div style={{ width: '100%', height: '100%', backgroundColor: displayColor }} />
                    ) : (
                        <div style={{
                            width: '100%', height: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#444', fontSize: '10px',
                        }}>?</div>
                    )}
                </div>

                {/* Slot info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: '12px',
                        color: '#ccc',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                    }}>
                        <span>{slot.slotName}</span>
                        <span style={{ fontSize: '10px', color: '#555' }}>#{slot.materialIndex}</span>
                    </div>
                    <div style={{
                        fontSize: '10px',
                        color: '#666',
                        marginTop: '1px',
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                    }}>
                        {slot.meshCount > 1 && <span>{slot.meshCount} meshes</span>}
                        {isCustomized && (
                            <span style={{
                                fontSize: '9px',
                                color: '#e8a736',
                                backgroundColor: '#e8a73620',
                                padding: '0 4px',
                                borderRadius: '2px',
                            }}>
                                modified
                            </span>
                        )}
                        {boundTo ? (
                            <span
                                style={{
                                    fontSize: '9px',
                                    color: '#b794f6',
                                    backgroundColor: '#b794f620',
                                    padding: '0 4px',
                                    borderRadius: '2px',
                                }}
                                title={`Material is bound to the "${boundTo}" property — states can override it`}
                            >
                                ${boundTo}
                            </span>
                        ) : hasGlbOverride && (
                            <span style={{
                                fontSize: '9px',
                                color: '#0a84ff',
                                backgroundColor: '#0a84ff20',
                                padding: '0 4px',
                                borderRadius: '2px',
                            }}>
                                GLB
                            </span>
                        )}
                    </div>
                </div>

                {/* Expand arrow */}
                <span style={{
                    fontSize: '9px',
                    color: '#555',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                    flexShrink: 0,
                }}>
                    ▶
                </span>
            </div>

            {/* Expanded detail */}
            {expanded && (
                <div style={{ padding: '8px 10px', borderTop: '1px solid #2a2a2a' }}>
                    {/* Base Color — routes through onColorSelect (state-aware,
                        auto-binds while a state is active). */}
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>Color</span>
                        <input
                            type="color"
                            value={displayColor ?? '#888888'}
                            onChange={(e) => onColorSelect(e.target.value)}
                            style={{
                                width: '28px', height: '22px', padding: 0,
                                border: '1px solid #555', borderRadius: '3px',
                                cursor: 'pointer', backgroundColor: 'transparent',
                            }}
                        />
                        <input
                            type="text"
                            value={colorDraft ?? (colorBoundTo ? resolvedBoundColor : (override?.baseColorFactor ?? ''))}
                            onChange={(e) => setColorDraft(e.target.value)}
                            onBlur={() => {
                                const v = (colorDraft ?? '').trim();
                                if (/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(v)) onColorSelect(v);
                                setColorDraft(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                            style={{ ...styles.input, width: '90px', fontFamily: 'monospace', fontSize: '11px' }}
                            placeholder={slot.albedoColor ?? 'default'}
                        />
                        {/* Binding selector — shows/edits the color's property
                            binding (rebind / new / unbind). Its "Unbind" replaces
                            the old restore button for bound slots. */}
                        <ColorBindingSelector
                            sceneObject={sceneObject}
                            slotIndex={slotIndex}
                            slotName={slot.slotName}
                            boundTo={colorBoundTo}
                            onChanged={onBindingChanged}
                        />
                        {/* Unbound slot with a literal override still gets the
                            classic restore-to-default-albedo affordance. */}
                        {!colorBoundTo && hasColorOverride && (
                            <button
                                onClick={() => onClearField('baseColorFactor')}
                                style={{
                                    padding: '2px 6px', backgroundColor: 'transparent',
                                    border: '1px solid #444', borderRadius: '3px',
                                    color: '#888', cursor: 'pointer', fontSize: '10px',
                                }}
                                title="Restore default color"
                            >
                                ↺
                            </button>
                        )}
                    </div>

                    {/* Variants — a swatch row when the slot's color is a variant
                        set, otherwise a small action to create one. */}
                    {variantSet ? (
                        <SlotVariantsRow
                            sceneObject={sceneObject}
                            slotIndex={slotIndex}
                            slotName={slot.slotName}
                            onRegen={onRegen}
                        />
                    ) : (
                        <div style={{ ...styles.fieldRow }}>
                            <span style={styles.label} />
                            <button
                                onClick={onCreateVariants}
                                style={{
                                    padding: '2px 8px', backgroundColor: 'transparent',
                                    border: '1px solid #444', borderRadius: '3px',
                                    color: '#888', cursor: 'pointer', fontSize: '10px',
                                }}
                                title="Create a swatch set of candidate colors for this material"
                            >
                                + Variants
                            </button>
                        </div>
                    )}

                    {/* UV Scale */}
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>UV Scale</span>
                        <input
                            type="number"
                            value={uvScale}
                            onChange={(e) => handleUvScaleChange(parseFloat(e.target.value) || 1)}
                            step={0.1}
                            min={0.1}
                            style={{ ...styles.input, width: '55px' }}
                        />
                        <input
                            type="range"
                            value={uvScale}
                            onChange={(e) => handleUvScaleChange(parseFloat(e.target.value))}
                            min={0.1}
                            max={10}
                            step={0.1}
                            style={{ flex: 1, cursor: 'pointer' }}
                        />
                        {hasUvOverride && (
                            <button
                                onClick={handleClearUv}
                                style={{
                                    padding: '2px 6px', backgroundColor: 'transparent',
                                    border: '1px solid #444', borderRadius: '3px',
                                    color: '#888', cursor: 'pointer', fontSize: '10px',
                                }}
                                title="Restore default UV scale"
                            >
                                ↺
                            </button>
                        )}
                    </div>

                    {/* Source GLB — edits buffer locally and commit on blur/Enter
                        via onSrcSelect (state-aware, auto-binds while a state is
                        active; the routed write round-trips through a commit, too
                        slow per-keystroke). */}
                    <div style={styles.fieldRow}>
                        <span style={styles.label}>Source</span>
                        <input
                            type="text"
                            value={srcDraft ?? (boundTo ? displaySrc : (override?.src ?? ''))}
                            onChange={(e) => setSrcDraft(e.target.value)}
                            onBlur={() => {
                                const committed = boundTo ? displaySrc : (override?.src ?? '');
                                if (srcDraft !== null && srcDraft !== committed) onSrcSelect(srcDraft);
                                setSrcDraft(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                            placeholder={boundTo ? 'native material' : 'GLB material URL'}
                            style={{ ...styles.input, flex: 1, fontSize: '11px' }}
                        />
                        <CatalogBrowseButton
                            entityType={EntityType.Material}
                            onSelect={(asset) => onSrcSelect(asset.url)}
                            title="Browse catalog materials"
                        />
                        {(boundTo ? displaySrc !== '' : hasGlbOverride) && (
                            <button
                                onClick={() => onClearField('src')}
                                style={{
                                    padding: '2px 6px', backgroundColor: 'transparent',
                                    border: '1px solid #444', borderRadius: '3px',
                                    color: '#888', cursor: 'pointer', fontSize: '10px',
                                }}
                                title={boundTo ? 'Use native material (keeps binding)' : 'Remove GLB override'}
                            >
                                ↺
                            </button>
                        )}
                    </div>

                    {/* Restore all button */}
                    {isCustomized && (
                        <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleRestore}
                                style={{
                                    padding: '3px 10px',
                                    backgroundColor: '#2a2a2a',
                                    border: '1px solid #444',
                                    borderRadius: '3px',
                                    color: '#aaa',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                }}
                                title="Remove all overrides for this slot"
                            >
                                Restore Default
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Renderer Editor Component
// ============================================================================

export interface RendererEditorProps {
    data: RendererData;
    onChange: (data: RendererData) => void;
    behavior: Renderer;
}

export const RendererEditor: React.FC<RendererEditorProps> = ({ data, onChange, behavior }) => {
    const [slots, setSlots] = useState<MaterialSlot[]>([]);
    const [loading, setLoading] = useState(false);
    // Detect material slots with previews
    const detectSlots = useCallback(async () => {
        setLoading(true);
        try {
            const detected = await MaterialPreviews.detectMaterialSlotsWithPreviews(
                behavior.sceneObject,
                PREVIEW_SIZE,
            );
            setSlots(detected);
        } catch (err) {
            console.warn('[RendererEditor] Failed to detect material slots:', err);
        } finally {
            setLoading(false);
        }
    }, [behavior]);

    // Detect slots on mount and when behavior changes
    useEffect(() => {
        void detectSlots();
    }, [detectSlots]);

    // Debounce timer for preview regeneration — prevents GPU readback spam
    // during rapid changes (e.g. dragging a color picker).
    const previewTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => () => clearTimeout(previewTimerRef.current), []);

    // Undo and Redo replay a commit without the handlers below, so they refresh
    // no thumbnail: the Color field and the mesh go back while the card keeps
    // the undone colour. The replayed commit announces itself while the replay
    // is still running (on this object, or on the Properties a slot is bound
    // to); regenerate every slot's thumbnail then.
    useEffect(() => {
        const onBehaviorChanged = () => {
            if (!getUndoManager().isUndoRedoing) return;
            clearTimeout(previewTimerRef.current);
            previewTimerRef.current = setTimeout(() => {
                MaterialPreviews.invalidateObjectPreviews(behavior.sceneObject.name);
                void detectSlots();
            }, 100);
        };
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);
        return () => window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);
    }, [behavior, detectSlots]);

    // Build override map: materialIndex -> MaterialData
    const overrideMap = new Map<number, MaterialData>();
    (data.materials ?? []).forEach((mat) => {
        overrideMap.set(mat.targetMaterialIndex ?? 0, mat);
    });

    // Live commit: apply changes immediately
    // changedSlot: if provided, only regenerate preview for that single slot
    const liveCommit = useCallback(async (
        newData: RendererData,
        changedSlot?: { index: number; name: string },
    ) => {
        try {
            // Styling what a reference brought in is an edit INSIDE it: copy
            // the asset into this scene first (SCENE_ROOT_SPEC §2.3
            // "Editing"). The object keeps its identity across the copy, so
            // `behavior` is still the one to commit into.
            if (!await copyForInsideEdit(behavior.sceneObject)) return;
            await behavior.commit(newData);

            // Debounce preview regeneration — the commit above applies the
            // material change instantly in the 3D viewport; the preview
            // thumbnail is a nice-to-have that can wait until edits settle.
            clearTimeout(previewTimerRef.current);
            previewTimerRef.current = setTimeout(() => void (async () => {
                if (changedSlot) {
                    const preview = await MaterialPreviews.regenerateSlotPreview(
                        behavior.sceneObject,
                        changedSlot.index,
                        changedSlot.name,
                        PREVIEW_SIZE,
                    );
                    if (preview != null) {
                        setSlots(prev => prev.map(s =>
                            s.materialIndex === changedSlot.index
                                ? { ...s, previewImage: preview }
                                : s
                        ));
                    }
                } else {
                    // Bulk change (e.g. restore all) — re-detect everything
                    MaterialPreviews.invalidateObjectPreviews(behavior.sceneObject.name);
                    void detectSlots();
                }
            })(), 100);
        } catch (err) {
            console.warn('[RendererEditor] Commit failed:', err);
        }
    }, [behavior, detectSlots]);

    const handleCastShadowsChange = (value: boolean) => {
        const newData = { ...data, castShadows: value };
        onChange(newData);
        void liveCommit(newData);
    };

    const handleOpacityChange = (value: number) => {
        const newData = { ...data, opacity: value };
        onChange(newData);
        void liveCommit(newData);
    };

    // Non-routed slot fields only (UV scale, whole-slot restore). Color and src
    // NEVER flow through here — they route through the shared state-aware helpers
    // below, so a bound slot's `{color}`/`$material` binding is never clobbered
    // by a stale liveCommit payload.
    const handleSlotOverrideChange = (slotIndex: number, slotName: string, updated: MaterialData | undefined) => {
        const newOverrides = new Map(overrideMap);
        if (updated) {
            updated.targetMaterialIndex = slotIndex;
            newOverrides.set(slotIndex, updated);
        } else {
            newOverrides.delete(slotIndex);
        }

        const materials = Array.from(newOverrides.values());
        const newData: RendererData = {
            ...data,
            materials: materials.length > 0 ? materials : undefined,
        };
        onChange(newData);
        void liveCommit(newData, { index: slotIndex, name: slotName });
    };

    // Debounced preview regen for a routed (color/src) edit — the shared helper
    // already committed to the behavior, so we DON'T re-commit; just refresh the
    // thumbnail once edits settle.
    const regenSlotPreview = useCallback((slotIndex: number, slotName: string) => {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = setTimeout(() => void (async () => {
            const preview = await MaterialPreviews.regenerateSlotPreview(
                behavior.sceneObject, slotIndex, slotName, PREVIEW_SIZE,
            );
            if (preview != null) {
                setSlots(prev => prev.map(s =>
                    s.materialIndex === slotIndex ? { ...s, previewImage: preview } : s,
                ));
            }
        })(), 100);
    }, [behavior]);

    const handleColorSelect = (slotIndex: number, slotName: string, hex: string) => {
        // "Recolour the placed chair makes it yours" (§2.3 "Editing"): the copy
        // runs first and the pick is re-applied to the scene's own node, which
        // is the same `SceneObject` an unpack leaves behind.
        if (needsReferenceCopy(behavior.sceneObject)) {
            void copyForInsideEdit(behavior.sceneObject)
                .then(copied => { if (copied) handleColorSelect(slotIndex, slotName, hex); });
            return;
        }

        // Normalize to #rrggbbaa: the native <input type="color"> emits 6-digit
        // #rrggbb (append opaque alpha), while the text field may already carry
        // an 8-digit value with alpha (leave it — appending 'FF' would corrupt it).
        const normalized = hex.length === 7 ? hex + 'FF' : hex;

        // If a variant swatch is active AND we're at Base, the Color-row picker
        // recolors THAT swatch in place (edits the shared variant definition),
        // keeping swatch and applied color in sync. Gate on Base: while a non-Base
        // state is active the pick routes into the state's OVERRIDE, so recoloring
        // the shared variant would let a state-scoped edit corrupt the definition
        // every state reads — matching handleClearField's editTargetState gate.
        // Capture the active index BEFORE applying — the apply moves activeIndex.
        const atBase = editTargetState(behavior.sceneObject) === undefined;
        const variantSet = atBase ? getMaterialVariantSet(behavior.sceneObject, slotIndex) : null;
        const activeVariant = variantSet && variantSet.activeIndex >= 0 ? variantSet.activeIndex : -1;

        void setMaterialColorForState(behavior.sceneObject, normalized, { slot: slotIndex })
            .then(({ errors }) => {
                if (errors.length > 0) {
                    console.warn('[RendererEditor] set material color failed:', errors);
                    return;
                }
                regenSlotPreview(slotIndex, slotName);
                if (activeVariant >= 0 && variantSet) {
                    void updateVariantOption(
                        behavior.sceneObject, variantSet.propertyName, activeVariant, normalized,
                    ).then(({ errors: vErrors }) => {
                        if (vErrors.length > 0) console.warn('[RendererEditor] recolor variant failed:', vErrors);
                    });
                }
            });
    };

    const handleSrcSelect = (slotIndex: number, slotName: string, url: string) => {
        // A material swap is the same kind of edit as a recolour (§2.3).
        if (needsReferenceCopy(behavior.sceneObject)) {
            void copyForInsideEdit(behavior.sceneObject)
                .then(copied => { if (copied) handleSrcSelect(slotIndex, slotName, url); });
            return;
        }
        void setMaterialSrcForState(behavior.sceneObject, url, { slot: slotIndex })
            .then(({ errors }) => {
                if (errors.length > 0) console.warn('[RendererEditor] set material src failed:', errors);
                else regenSlotPreview(slotIndex, slotName);
            });
    };

    // "Variants" action — create (or adopt) a variant set on this slot's color.
    // Emits a Properties BEHAVIOR_CHANGED that the SlotVariantsRow subscribes to;
    // we re-detect slots so the card flips from the action button to the row.
    const handleCreateVariants = (slotIndex: number, slotName: string) => {
        void createMaterialVariantSet(behavior.sceneObject, { slot: slotIndex, materialName: slotName })
            .then(({ errors }) => {
                if (errors.length > 0) console.warn('[RendererEditor] create variant set failed:', errors);
                else {
                    regenSlotPreview(slotIndex, slotName);
                    void detectSlots();
                }
            });
    };

    // Clear a routed field. While a state is active on a bound slot, remove the
    // state override (follow Base). At Base (or unbound), write the "cleared"
    // value: '' src = native material, and for color remove the literal override.
    const handleClearField = (slotIndex: number, slotName: string, field: 'baseColorFactor' | 'src') => {
        const override = overrideMap.get(slotIndex);
        const authored = override?.[field];
        const boundProp = typeof authored === 'string' && isPropertyBinding(authored)
            ? boundPropertyName(authored)
            : null;

        if (boundProp && editTargetState(behavior.sceneObject) !== undefined) {
            void clearStateValue(behavior.sceneObject, boundProp).then(({ cleared }) => {
                if (cleared) regenSlotPreview(slotIndex, slotName);
            });
            return;
        }

        // Base / unbound: remove the literal override for this field.
        if (field === 'src') {
            handleSrcSelect(slotIndex, slotName, ''); // '' = native material
            return;
        }
        if (!override) return;
        const { baseColorFactor: _c, ...rest } = override;
        const hasOtherProps = rest.src != null || rest.texture_transform != null;
        handleSlotOverrideChange(slotIndex, slotName, hasOtherProps ? rest : undefined);
    };

    const hasAnyOverrides = overrideMap.size > 0;

    const handleRestoreAll = () => {
        const newData: RendererData = { ...data, materials: undefined };
        onChange(newData);
        void liveCommit(newData);
    };

    return (
        <div>
            {/* Shadows */}
            <div style={styles.section}>
                <div style={styles.fieldRow}>
                    <span style={{ ...styles.label, minWidth: '100px' }}>Cast Shadows</span>
                    <input
                        type="checkbox"
                        checked={data.castShadows ?? true}
                        onChange={(e) => handleCastShadowsChange(e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                </div>
                <div style={styles.fieldRow}>
                    <span style={{ ...styles.label, minWidth: '100px' }}>Opacity</span>
                    <input
                        type="number"
                        value={data.opacity ?? 1}
                        onChange={(e) => handleOpacityChange(Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)))}
                        step={0.05}
                        min={0}
                        max={1}
                        style={{ ...styles.input, width: '55px' }}
                    />
                    <input
                        type="range"
                        value={data.opacity ?? 1}
                        onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
                        min={0}
                        max={1}
                        step={0.01}
                        style={{ flex: 1, cursor: 'pointer' }}
                    />
                </div>
            </div>

            {/* Material Slots */}
            <div style={styles.section}>
                <div style={{
                    ...styles.sectionHeader,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <span>Materials</span>
                    {hasAnyOverrides && (
                        <button
                            onClick={handleRestoreAll}
                            style={{
                                padding: '1px 8px',
                                backgroundColor: 'transparent',
                                border: '1px solid #444',
                                borderRadius: '3px',
                                color: '#888',
                                cursor: 'pointer',
                                fontSize: '10px',
                                textTransform: 'none' as const,
                                fontWeight: 'normal',
                                letterSpacing: 'normal',
                            }}
                        >
                            Restore All
                        </button>
                    )}
                </div>

                {loading && slots.length === 0 ? (
                    <div style={{
                        color: '#666', fontSize: '11px', fontStyle: 'italic',
                        padding: '12px 8px', textAlign: 'center',
                    }}>
                        Detecting materials...
                    </div>
                ) : slots.length === 0 ? (
                    <div style={{
                        color: '#666', fontSize: '11px', fontStyle: 'italic',
                        padding: '8px', backgroundColor: '#1e1e1e', borderRadius: '4px',
                    }}>
                        No material slots found. Add a Model behavior first.
                    </div>
                ) : (
                    slots.map((slot) => (
                        <SlotCard
                            key={slot.materialIndex ?? slot.slotName}
                            slot={slot}
                            override={overrideMap.get(slot.materialIndex ?? 0)}
                            sceneObject={behavior.sceneObject}
                            onOverrideChange={(updated) =>
                                handleSlotOverrideChange(slot.materialIndex ?? 0, slot.slotName, updated)
                            }
                            onColorSelect={(hex) => handleColorSelect(slot.materialIndex ?? 0, slot.slotName, hex)}
                            onSrcSelect={(url) => handleSrcSelect(slot.materialIndex ?? 0, slot.slotName, url)}
                            onClearField={(field) => handleClearField(slot.materialIndex ?? 0, slot.slotName, field)}
                            onCreateVariants={() => handleCreateVariants(slot.materialIndex ?? 0, slot.slotName)}
                            onRegen={() => regenSlotPreview(slot.materialIndex ?? 0, slot.slotName)}
                            onBindingChanged={() => {
                                regenSlotPreview(slot.materialIndex ?? 0, slot.slotName);
                                void detectSlots();
                            }}
                        />
                    ))
                )}
            </div>
        </div>
    );
};
